import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Advice,
    Button,
    Checkbox,
    Icon,
    Loader,
    Markdown,
    Modal,
    Progress,
    Tabs,
    Text,
    Tooltip,
} from '@Sage/hoi-poi-ui';

import { EntityListActions, ServerListActions } from 'actions';
import { COMPANIES } from 'constants/Entities';
import { subscribe } from 'lib/EventBuser';
import { LEADS_MODAL_ACCOUNT_CREATED } from 'lib/events';
import { LeadsService } from 'services';
import { getLiteral, getLiteralWithParameters, injectDebugLiterals } from 'utils/getLiteral';
import { getBrowserGeolocation, resolveMapFallBackCenterFromUserData } from 'utils/map';

import LeadList from './LeadList';
import LeadsMap from './LeadsMap';
import LeadsQuickFilters from './LeadsQuickFilters';
import PlaceSearch from './PlaceSearch';
import ReviewQueue from './ReviewQueue';
import {
    CATEGORY_OPTIONS,
    DEFAULT_CREDIT_BALANCE,
    LEADS_MODAL_STEPS,
    MAX_RESULT_OPTIONS,
    RADIUS_OPTIONS,
    RESULT_BUCKETS,
} from './constants';
import {
    buildPreviewRequest,
    createCompaniesSequentially,
    getSelectedLeads,
    splitByBucket,
    toAccountCrudData,
} from './helpers';
import { LEADS_DEBUG_LITERALS, LEADS_LITERALS } from './literals';

import './styles.scss';

injectDebugLiterals(LEADS_DEBUG_LITERALS);

const DEFAULT_ORIGIN = { lat: 41.391067, lng: 2.1288348 };
const DEFAULT_CATEGORY = CATEGORY_OPTIONS[0].value;
const DEFAULT_MAX_RESULTS = MAX_RESULT_OPTIONS[1].value;
const DEFAULT_RADIUS_KM = RADIUS_OPTIONS[1].value;
const getDefaultOrigin = () => ({ ...DEFAULT_ORIGIN });
const getDefaultProgress = () => ({ current: 0, total: 0 });

const isFiniteNumber = (value) => Number.isFinite(Number(value));
const isValidCoordinate = (value, min, max) => {
    const number = Number(value);
    return Number.isFinite(number) && number >= min && number <= max;
};

const LeadsModal = ({ isOpen, onClose, onReviewCreate }) => {
    const dispatch = useDispatch();
    const countries = useSelector((state) => state.serverList?.tblCountries?.data || []);
    const userLatitude = useSelector((state) => state.config.userData?.userLatitude);
    const userLongitude = useSelector((state) => state.config.userData?.userLongitude);
    const defaultCountryId = useSelector((state) => state.config.userData?.defaultCountryId);
    const companiesMapLastPosition = useSelector(
        (state) => state.entityMap?.[COMPANIES.entity]?.lastPosition ?? null,
    );
    const companiesMapLastPositionRef = useRef(companiesMapLastPosition);
    companiesMapLastPositionRef.current = companiesMapLastPosition;
    const countriesRef = useRef(countries);
    countriesRef.current = countries;

    const [step, setStep] = useState(LEADS_MODAL_STEPS.SETUP);
    const [category, setCategory] = useState(DEFAULT_CATEGORY);
    const [maxResults, setMaxResults] = useState(DEFAULT_MAX_RESULTS);
    const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
    const [origin, setOrigin] = useState(getDefaultOrigin);
    const [includeDuplicates, setIncludeDuplicates] = useState(false);
    /** After any prospecting run with "show potential duplicates" on; keeps bucket tabs across reruns until modal reset. */
    const [duplicateTabsEverShown, setDuplicateTabsEverShown] = useState(false);
    const [leads, setLeads] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [activeBucket, setActiveBucket] = useState(RESULT_BUCKETS.NEW);
    const [creditBalance, setCreditBalance] = useState(DEFAULT_CREDIT_BALANCE);
    const [confirmCreditsOpen, setConfirmCreditsOpen] = useState(false);
    const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
    const [reviewRows, setReviewRows] = useState([]);
    const [progress, setProgress] = useState(getDefaultProgress);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const isOpenRef = useRef(isOpen);
    const isClosingRef = useRef(!isOpen);
    const requestVersionRef = useRef(0);

    const beginClose = useCallback(() => {
        if (!isClosingRef.current) {
            requestVersionRef.current += 1;
        }
        isClosingRef.current = true;
    }, []);

    const isRequestCurrent = useCallback(
        (requestVersion) =>
            isOpenRef.current &&
            !isClosingRef.current &&
            requestVersion === requestVersionRef.current,
        [],
    );

    useEffect(() => {
        isOpenRef.current = isOpen;

        if (isOpen) {
            isClosingRef.current = false;
            return;
        }

        beginClose();
    }, [beginClose, isOpen]);

    /** Same resolution order as `components/Map`: browser → profile lat/lng → country geocode → companies map last center → default. */
    useEffect(() => {
        if (!isOpen) return undefined;

        let cancelled = false;

        const applyOriginIfValid = (candidate) => {
            if (cancelled || !isOpenRef.current) return false;
            if (candidate == null) return false;
            const lat = Number(candidate.lat);
            const lng = Number(candidate.lng);
            if (!isValidCoordinate(lat, -90, 90) || !isValidCoordinate(lng, -180, 180))
                return false;
            setOrigin({ lat, lng });
            return true;
        };

        (async () => {
            try {
                const pos = await getBrowserGeolocation();
                if (applyOriginIfValid(pos)) return;
            } catch {
                // continue with profile / country / session fallbacks
            }

            try {
                const resolved = await resolveMapFallBackCenterFromUserData({
                    userLatitude,
                    userLongitude,
                    defaultCountryId,
                    countries: countriesRef.current,
                    fetchCountriesList: () => dispatch(ServerListActions.getList('tblCountries')),
                });
                if (applyOriginIfValid(resolved)) return;
            } catch {
                // resolver already logs; continue
            }

            const last = companiesMapLastPositionRef.current;
            if (
                last &&
                isValidCoordinate(Number(last.lat), -90, 90) &&
                isValidCoordinate(Number(last.lng), -180, 180)
            ) {
                applyOriginIfValid({ lat: Number(last.lat), lng: Number(last.lng) });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [defaultCountryId, dispatch, isOpen, userLatitude, userLongitude]);

    useEffect(() => {
        return subscribe(LEADS_MODAL_ACCOUNT_CREATED, ({ rowId, creationDate }) => {
            setReviewRows((current) =>
                current.map((row) =>
                    String(row.id) === String(rowId)
                        ? { ...row, status: 'created', creationDate }
                        : row,
                ),
            );
            dispatch(EntityListActions.init(COMPANIES, false, null, null, null, true));
        });
    }, [dispatch]);

    const buckets = useMemo(() => splitByBucket(leads), [leads]);
    const showDuplicateTabs = duplicateTabsEverShown || includeDuplicates;
    const activeLeads = useMemo(() => {
        if (!showDuplicateTabs) {
            return buckets.new;
        }
        return activeBucket === RESULT_BUCKETS.DUPLICATES ? buckets.duplicates : buckets.new;
    }, [activeBucket, buckets.duplicates, buckets.new, showDuplicateTabs]);

    const duplicateBucketTabs = useMemo(
        () =>
            showDuplicateTabs
                ? [
                      {
                          key: RESULT_BUCKETS.NEW,
                          title: `${getLiteral(LEADS_LITERALS.new)} (${buckets.new.length})`,
                          content: <div className="fm-leads-modal__bucket-tab-spacer" />,
                      },
                      {
                          key: RESULT_BUCKETS.DUPLICATES,
                          title: `${getLiteral(LEADS_LITERALS.potentialDuplicates)} (${buckets.duplicates.length})`,
                          content: <div className="fm-leads-modal__bucket-tab-spacer" />,
                      },
                  ]
                : [],
        [showDuplicateTabs, buckets.new.length, buckets.duplicates.length],
    );
    const selectedLeads = useMemo(() => getSelectedLeads(leads, selectedIds), [leads, selectedIds]);
    const selectedCost = selectedLeads.length;
    const projectedBalance = creditBalance - selectedCost;
    const canEnhance = selectedCost > 0 && projectedBalance >= 0;
    const canRunPreview =
        isValidCoordinate(origin.lat, -90, 90) &&
        isValidCoordinate(origin.lng, -180, 180) &&
        isFiniteNumber(radiusKm) &&
        Number(radiusKm) > 0;
    const categoryOptions = useMemo(
        () =>
            CATEGORY_OPTIONS.map((option) => ({
                ...option,
                label: getLiteral(LEADS_LITERALS[option.labelKey]),
            })),
        [],
    );
    const pendingRows = useMemo(
        () => reviewRows.filter((row) => row.status === 'pending'),
        [reviewRows],
    );
    const successfulRows = useMemo(
        () => reviewRows.filter((row) => row.status !== 'failed'),
        [reviewRows],
    );
    const failedRows = useMemo(
        () => reviewRows.filter((row) => row.status === 'failed'),
        [reviewRows],
    );
    const createdRows = useMemo(
        () => reviewRows.filter((row) => row.status === 'created'),
        [reviewRows],
    );

    const reset = useCallback(() => {
        setStep(LEADS_MODAL_STEPS.SETUP);
        setCategory(DEFAULT_CATEGORY);
        setMaxResults(DEFAULT_MAX_RESULTS);
        setRadiusKm(DEFAULT_RADIUS_KM);
        setOrigin(getDefaultOrigin());
        setIncludeDuplicates(false);
        setLeads([]);
        setSelectedIds([]);
        setActiveBucket(RESULT_BUCKETS.NEW);
        setCreditBalance(DEFAULT_CREDIT_BALANCE);
        setReviewRows([]);
        setDuplicateTabsEverShown(false);
        setProgress(getDefaultProgress());
        setLoading(false);
        setError('');
        setConfirmCreditsOpen(false);
        setBulkCreateOpen(false);
    }, []);

    const handleClose = useCallback(() => {
        beginClose();
        onClose();
    }, [beginClose, onClose]);

    const handleBackFromReviewToPreview = useCallback(() => {
        setStep(LEADS_MODAL_STEPS.PREVIEW);
        setError('');
        setConfirmCreditsOpen(false);
        setBulkCreateOpen(false);
    }, []);

    const handleAfterClose = useCallback(() => {
        if (isOpenRef.current) return;
        LeadsService.cancelRequests();
        reset();
    }, [reset]);

    const runPreview = useCallback(() => {
        if (!canRunPreview) {
            setError(getLiteral(LEADS_LITERALS.validOriginRequired));
            return;
        }

        const requestVersion = requestVersionRef.current;
        setLoading(true);
        setError('');
        LeadsService.nearbyPlaces(
            buildPreviewRequest({
                category,
                includeDuplicates,
                maxResults,
                origin,
                radiusKm,
            }),
            origin,
        )
            .then((items) => {
                if (!isRequestCurrent(requestVersion)) return;
                setLeads(items);
                setSelectedIds([]);
                if (includeDuplicates) {
                    setDuplicateTabsEverShown(true);
                }
                setActiveBucket(RESULT_BUCKETS.NEW);
                setStep(LEADS_MODAL_STEPS.PREVIEW);
                if (!items.length) setError(getLiteral(LEADS_LITERALS.noCoordinatesReturned));
            })
            .catch((requestError) => {
                if (
                    LeadsService.isCanceledRequest(requestError) ||
                    !isRequestCurrent(requestVersion)
                ) {
                    return;
                }
                setError(getLiteral(LEADS_LITERALS.previewFailed));
            })
            .finally(() => {
                if (isRequestCurrent(requestVersion)) {
                    setLoading(false);
                }
            });
    }, [
        canRunPreview,
        category,
        includeDuplicates,
        isRequestCurrent,
        maxResults,
        origin,
        radiusKm,
    ]);

    const toggleLead = useCallback((id) => {
        setSelectedIds((current) => {
            const isSelected = current.some((sid) => String(sid) === String(id));
            return isSelected
                ? current.filter((item) => String(item) !== String(id))
                : [...current, id];
        });
    }, []);

    const toggleIncludeDuplicates = useCallback(() => {
        setIncludeDuplicates((current) => !current);
    }, []);

    const runEnrichment = useCallback(() => {
        const requestVersion = requestVersionRef.current;
        setConfirmCreditsOpen(false);
        setStep(LEADS_MODAL_STEPS.ENRICHING);
        setProgress({ current: selectedLeads.length, total: selectedLeads.length });
        setError('');
        setLoading(true);

        LeadsService.placeToAccountForm(selectedLeads)
            .then((items) => {
                if (!isRequestCurrent(requestVersion)) return;
                const successful = items.filter((item) => item.status !== 'failed').length;
                setCreditBalance((current) => current - successful);
                setReviewRows(items);
                setProgress({ current: selectedLeads.length, total: selectedLeads.length });
                setStep(LEADS_MODAL_STEPS.REVIEW);
            })
            .catch((requestError) => {
                if (
                    LeadsService.isCanceledRequest(requestError) ||
                    !isRequestCurrent(requestVersion)
                ) {
                    return;
                }
                setError(getLiteral(LEADS_LITERALS.enhancementFailed));
                setStep(LEADS_MODAL_STEPS.PREVIEW);
            })
            .finally(() => {
                if (isRequestCurrent(requestVersion)) {
                    setLoading(false);
                }
            });
    }, [isRequestCurrent, selectedLeads]);

    const handleReviewCreate = useCallback(
        (row) => {
            onReviewCreate(row, toAccountCrudData(row));
        },
        [onReviewCreate],
    );

    const createRemaining = useCallback(() => {
        const requestVersion = requestVersionRef.current;
        setBulkCreateOpen(false);
        setLoading(true);
        setError('');
        setProgress({ current: 0, total: pendingRows.length });

        createCompaniesSequentially({
            rows: pendingRows,
            getError: () => getLiteral(LEADS_LITERALS.accountCreationFailed),
            onProgress: () => {
                if (!isRequestCurrent(requestVersion)) return;
                setProgress((current) => ({
                    ...current,
                    current: current.current + 1,
                }));
            },
        })
            .then((createdOrFailedRows) => {
                if (!isRequestCurrent(requestVersion)) return;
                setReviewRows((current) =>
                    current.map((row) => {
                        const updated = createdOrFailedRows.find((item) => item.id === row.id);
                        return updated || row;
                    }),
                );
                dispatch(EntityListActions.init(COMPANIES, false, null, null, null, true));
            })
            .finally(() => {
                if (isRequestCurrent(requestVersion)) {
                    setLoading(false);
                }
            });
    }, [dispatch, isRequestCurrent, pendingRows]);

    const renderControls = () => (
        <div className="fm-leads-modal__controls">
            <div className="fm-leads-modal__controls-left">
                <div className="fm-leads-modal__search-place">
                    <PlaceSearch onSelectPlace={setOrigin} />
                </div>
                <LeadsQuickFilters
                    category={category}
                    categoryOptions={categoryOptions}
                    maxResults={maxResults}
                    maxResultOptions={MAX_RESULT_OPTIONS}
                    radiusKm={radiusKm}
                    radiusOptions={RADIUS_OPTIONS}
                    onChangeCategory={setCategory}
                    onChangeMaxResults={setMaxResults}
                    onChangeRadiusKm={setRadiusKm}
                />
            </div>
            <div className="fm-leads-modal__controls-right">
                <div className="fm-leads-modal__checkbox-row" onClick={toggleIncludeDuplicates}>
                    <span
                        className="fm-leads-modal__checkbox-hitbox"
                        onClick={(event) => event.stopPropagation()}
                        role="presentation"
                    >
                        <Checkbox
                            color="actionMajor"
                            checked={includeDuplicates}
                            onChange={toggleIncludeDuplicates}
                        />
                    </span>
                    <Text type="body">{getLiteral(LEADS_LITERALS.showPotentialDuplicates)}</Text>
                </div>
                <Button
                    type="primary"
                    isDisabled={!canRunPreview}
                    onClick={runPreview}
                    isLoading={loading}
                >
                    {getLiteral(LEADS_LITERALS.runProspecting)}
                </Button>
            </div>
        </div>
    );

    const renderSetup = () => (
        <div className="fm-leads-modal__step fm-leads-modal__step--setup">
            <Advice type="info" showIcon>
                {getLiteral(LEADS_LITERALS.previewIsFree)}
            </Advice>
            {renderControls()}
            <LeadsMap
                activeLeads={activeLeads}
                selectedIds={selectedIds}
                origin={origin}
                radiusKm={radiusKm}
                onOriginChange={setOrigin}
                onToggleLead={toggleLead}
            />
        </div>
    );

    const renderPreview = () => (
        <div className="fm-leads-modal__step fm-leads-modal__step--preview">
            {renderControls()}
            <div className="fm-leads-modal__split">
                <LeadsMap
                    activeLeads={activeLeads}
                    selectedIds={selectedIds}
                    origin={origin}
                    radiusKm={radiusKm}
                    onOriginChange={setOrigin}
                    onToggleLead={toggleLead}
                />
                <div className="fm-leads-modal__list-column">
                    {duplicateBucketTabs.length > 0 && (
                        <Tabs
                            className="fm-leads-modal__bucket-tabs"
                            activeKey={activeBucket}
                            onChange={setActiveBucket}
                            tabs={duplicateBucketTabs}
                        />
                    )}
                    <LeadList
                        activeLeads={activeLeads}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                    />
                </div>
            </div>
            <div className="fm-leads-modal__summary">
                <div className="fm-leads-modal__summary-metrics">
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral(LEADS_LITERALS.selected)}:</span>
                        <Text type="caption" bold as="span">
                            {selectedCost}
                        </Text>
                    </Text>
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral(LEADS_LITERALS.currentBalance)}:</span>
                        <Text type="caption" bold as="span">
                            {creditBalance}
                        </Text>
                    </Text>
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral(LEADS_LITERALS.maxCost)}:</span>
                        <Text type="caption" bold as="span">
                            {selectedCost}
                        </Text>
                    </Text>
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral(LEADS_LITERALS.projectedBalance)}:</span>
                        <Text type="caption" bold as="span">
                            {projectedBalance}
                        </Text>
                    </Text>
                </div>
                {projectedBalance < 0 && (
                    <Text type="caption">{getLiteral(LEADS_LITERALS.insufficientCredits)}</Text>
                )}
                <Button
                    type="primary"
                    isDisabled={!canEnhance}
                    onClick={() => setConfirmCreditsOpen(true)}
                >
                    {getLiteral(LEADS_LITERALS.enhanceSelected)}
                </Button>
            </div>
        </div>
    );

    const renderEnriching = () => (
        <div className="fm-leads-modal__progress">
            <Loader size="big" />
            <Text type="h6">{getLiteral(LEADS_LITERALS.enhancementProgress)}</Text>
            <Text type="body" className="fm-text-inlineGap">
                <span>{getLiteral(LEADS_LITERALS.enhancementSubmitted)}:</span>
                <span>{progress.current}</span>
                <span>/</span>
                <span>{progress.total}</span>
            </Text>
            <Progress value={progress.current} max={progress.total || 1} />
        </div>
    );

    const renderReview = () => (
        <div className="fm-leads-modal__step fm-leads-modal__step--review">
            <div className="fm-leads-modal__review-summary">
                <Text type="h6">{getLiteral(LEADS_LITERALS.reviewEnrichedLeads)}</Text>
                <Text type="caption" className="fm-text-inlineGap">
                    <span>
                        {getLiteral(LEADS_LITERALS.successful)}: {successfulRows.length}.
                    </span>
                    <span>
                        {getLiteral(LEADS_LITERALS.failed)}: {failedRows.length}.
                    </span>
                    <span>
                        {getLiteral(LEADS_LITERALS.maxCost)}: {successfulRows.length}.
                    </span>
                    <span>
                        {getLiteral(LEADS_LITERALS.currentBalance)}: {creditBalance}.
                    </span>
                </Text>
                <Advice type="info" showIcon>
                    {getLiteral(LEADS_LITERALS.createdRecordsTip)}
                </Advice>
                {loading && progress.total > 0 && (
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral(LEADS_LITERALS.creatingAccounts)}:</span>
                        <span>{progress.current}</span>
                        <span>/</span>
                        <span>{progress.total}</span>
                    </Text>
                )}
            </div>
            <ReviewQueue rows={reviewRows} onReviewCreate={handleReviewCreate} />
            <div className="fm-leads-modal__footer-actions">
                <Button
                    type="secondary"
                    isDisabled={loading}
                    onClick={handleBackFromReviewToPreview}
                >
                    {getLiteral(LEADS_LITERALS.backToProspectingResults)}
                </Button>
                <Button
                    type="primary"
                    isDisabled={!pendingRows.length || loading}
                    isLoading={loading}
                    onClick={() => setBulkCreateOpen(true)}
                >
                    <span className="fm-leads-modal__button-with-tooltip">
                        <span>{getLiteral(LEADS_LITERALS.createRemaining)}</span>
                        <Tooltip
                            placement="top"
                            content={getLiteral(LEADS_LITERALS.createRemainingTooltip)}
                        >
                            <span
                                className="fm-leads-modal__button-info"
                                onClick={(event) => event.stopPropagation()}
                                role="presentation"
                            >
                                <Icon name="infoOutlined" size="small" />
                            </span>
                        </Tooltip>
                    </span>
                </Button>
            </div>
        </div>
    );

    const renderComplete = () => (
        <div className="fm-leads-modal__complete">
            <Text type="h6">{getLiteral(LEADS_LITERALS.mapProspectingComplete)}</Text>
            <Text type="body" className="fm-text-inlineGap">
                <span>{createdRows.length}</span>
                <span>{getLiteral(LEADS_LITERALS.accountsCreated)}.</span>
                <span>{getLiteral(LEADS_LITERALS.failedNotCharged)}</span>
            </Text>
            <Button type="primary" onClick={handleClose}>
                {getLiteral(LEADS_LITERALS.backToAccounts)}
            </Button>
        </div>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                onAfterClose={handleAfterClose}
                onRequestClose={handleClose}
                size="full"
                title={getLiteral(LEADS_LITERALS.prospectFromMap)}
                useHeader
                classes={{ content: 'fm-leads-modal__content' }}
            >
                <div className="fm-leads-modal">
                    {error && (
                        <Advice type="warning" showIcon>
                            {error}
                        </Advice>
                    )}
                    {step === LEADS_MODAL_STEPS.SETUP && renderSetup()}
                    {step === LEADS_MODAL_STEPS.PREVIEW && renderPreview()}
                    {step === LEADS_MODAL_STEPS.ENRICHING && renderEnriching()}
                    {step === LEADS_MODAL_STEPS.REVIEW && renderReview()}
                    {step === LEADS_MODAL_STEPS.COMPLETE && renderComplete()}
                </div>
            </Modal>
            <Modal
                isOpen={confirmCreditsOpen}
                title={getLiteral(LEADS_LITERALS.useCredits)}
                confirmText={getLiteral(LEADS_LITERALS.confirmAndEnhance)}
                cancelText={getLiteral('action_cancel')}
                onRequestClose={() => setConfirmCreditsOpen(false)}
                onCancel={() => setConfirmCreditsOpen(false)}
                onConfirm={runEnrichment}
            >
                <Markdown
                    className="fm-leads-modal__credits-copy"
                    content={getLiteralWithParameters(LEADS_LITERALS.useCreditsMarkdown, [
                        creditBalance,
                        selectedCost,
                        selectedCost,
                        projectedBalance,
                    ])}
                />
            </Modal>
            <Modal
                isOpen={bulkCreateOpen}
                title={getLiteral(LEADS_LITERALS.createRemainingAccounts)}
                confirmText={getLiteral(LEADS_LITERALS.createRemaining)}
                cancelText={getLiteral('action_cancel')}
                onRequestClose={() => setBulkCreateOpen(false)}
                onCancel={() => setBulkCreateOpen(false)}
                onConfirm={createRemaining}
            >
                <Text type="body">
                    {getLiteral(LEADS_LITERALS.createRemainingDescription)} {pendingRows.length}
                </Text>
            </Modal>
        </>
    );
};

export default memo(LeadsModal);
